'use client';
import React, { useState, useRef, useCallback } from 'react';
import {
  X,
  Upload,
  FileSpreadsheet,
  AlertCircle,
  CheckCircle2,
  ChevronRight,
  Loader2,
  ArrowLeft,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import { insertClaimItem } from '@/lib/claimService';
import { toast } from 'sonner';

interface ImportItemsModalProps {
  rooms: { id: string; name: string }[];
  members: { id: string; name: string; color: string }[];
  claimId?: string;
  onClose: () => void;
  onImportComplete: (count: number) => void;
}

// The item fields users can map columns to
const ITEM_FIELDS: { key: string; label: string; required?: boolean; description: string }[] = [
  { key: 'name', label: 'Item Name', required: true, description: 'Name of the item (required)' },
  { key: 'description', label: 'Description', description: 'Additional details about the item' },
  { key: 'category', label: 'Category', description: 'Item category (e.g. Electronics, Furniture)' },
  { key: 'quantity', label: 'Quantity', description: 'Number of units (defaults to 1)' },
  { key: 'unitPrice', label: 'Unit Price ($)', description: 'Replacement cost per unit' },
  { key: 'location', label: 'Room / Location', description: 'Where the item was located' },
  { key: 'addedBy', label: 'Added By', description: 'Family member who added this item' },
  { key: 'condition', label: 'Condition', description: 'Damage condition of the item' },
  { key: 'estimatedAge', label: 'Estimated Age', description: 'Age of the item (e.g. 2 years)' },
  { key: 'notes', label: 'Notes', description: 'Additional notes or serial numbers' },
];

type Step = 'upload' | 'map' | 'preview' | 'importing';

interface ParsedRow {
  [key: string]: string;
}

interface MappedItem {
  name: string;
  description: string;
  category: string;
  quantity: number;
  unitPrice: number;
  location: string;
  addedBy: string;
  condition: string;
  estimatedAge: string;
  notes: string;
  valid: boolean;
  error?: string;
}

export default function ImportItemsModal({ rooms, members, claimId, onClose, onImportComplete }: ImportItemsModalProps) {
  const [step, setStep] = useState<Step>('upload');
  const [isDragging, setIsDragging] = useState(false);
  const [fileName, setFileName] = useState('');
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [columnMap, setColumnMap] = useState<Record<string, string>>({}); // fieldKey -> columnHeader
  const [mappedItems, setMappedItems] = useState<MappedItem[]>([]);
  const [importProgress, setImportProgress] = useState(0);
  const [importErrors, setImportErrors] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const defaultLocation = rooms[0]?.name ?? '';
  const defaultMember = members[0]?.name ?? '';

  // ── File parsing ──────────────────────────────────────────────────────────

  const parseFile = useCallback((file: File) => {
    const ext = file.name.split('.').pop()?.toLowerCase();
    setFileName(file.name);

    if (ext === 'csv') {
      Papa.parse<ParsedRow>(file, {
        header: true,
        skipEmptyLines: true,
        complete: (result) => {
          const hdrs = result.meta.fields ?? [];
          setHeaders(hdrs);
          setRows(result.data as ParsedRow[]);
          // Auto-map obvious columns
          setColumnMap(autoMap(hdrs));
          setStep('map');
        },
        error: () => toast.error('Failed to parse CSV file'),
      });
    } else if (ext === 'xlsx' || ext === 'xls') {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json<ParsedRow>(sheet, { defval: '' });
          if (jsonData.length === 0) { toast.error('Spreadsheet appears to be empty'); return; }
          const hdrs = Object.keys(jsonData[0]);
          setHeaders(hdrs);
          setRows(jsonData);
          setColumnMap(autoMap(hdrs));
          setStep('map');
        } catch {
          toast.error('Failed to parse Excel file');
        }
      };
      reader.readAsArrayBuffer(file);
    } else {
      toast.error('Please upload a .csv, .xlsx, or .xls file');
    }
  }, []);

  // Auto-map column headers to item fields using fuzzy matching
  function autoMap(hdrs: string[]): Record<string, string> {
    const map: Record<string, string> = {};
    const normalize = (s: string) => s.toLowerCase().replace(/[\s_\-]/g, '');

    const aliases: Record<string, string[]> = {
      name: ['name', 'item', 'itemname', 'product', 'description', 'title'],
      description: ['description', 'desc', 'details', 'detail', 'notes', 'info'],
      category: ['category', 'cat', 'type', 'itemtype', 'itemcategory', 'group'],
      quantity: ['quantity', 'qty', 'count', 'amount', 'num', 'number'],
      unitPrice: ['unitprice', 'price', 'cost', 'value', 'replacementcost', 'replacementprice', 'retail'],
      location: ['location', 'room', 'area', 'place', 'where'],
      addedBy: ['addedby', 'member', 'person', 'who', 'addedby', 'family'],
      condition: ['condition', 'status', 'damage', 'state'],
      estimatedAge: ['estimatedage', 'age', 'years', 'old'],
      notes: ['notes', 'note', 'comments', 'comment', 'remarks'],
    };

    for (const [fieldKey, aliasList] of Object.entries(aliases)) {
      for (const hdr of hdrs) {
        if (aliasList.includes(normalize(hdr))) {
          if (!map[fieldKey]) map[fieldKey] = hdr;
        }
      }
    }
    return map;
  }

  // ── Drag & drop ───────────────────────────────────────────────────────────

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) parseFile(file);
  }, [parseFile]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) parseFile(file);
  };

  // ── Mapping ───────────────────────────────────────────────────────────────

  const handleMapChange = (fieldKey: string, columnHeader: string) => {
    setColumnMap((prev) => {
      const next = { ...prev };
      if (columnHeader === '') {
        delete next[fieldKey];
      } else {
        next[fieldKey] = columnHeader;
      }
      return next;
    });
  };

  const buildPreview = () => {
    const items: MappedItem[] = rows.map((row) => {
      const get = (fieldKey: string) => {
        const col = columnMap[fieldKey];
        return col ? (row[col] ?? '').toString().trim() : '';
      };

      const name = get('name');
      const qty = parseInt(get('quantity') || '1', 10);
      const price = parseFloat(get('unitPrice').replace(/[$,]/g, '') || '0');

      return {
        name,
        description: get('description'),
        category: get('category'),
        quantity: isNaN(qty) || qty < 1 ? 1 : qty,
        unitPrice: isNaN(price) ? 0 : price,
        location: get('location') || defaultLocation,
        addedBy: get('addedBy') || defaultMember,
        condition: get('condition') || 'Destroyed',
        estimatedAge: get('estimatedAge'),
        notes: get('notes'),
        valid: !!name,
        error: !name ? 'Item name is required' : undefined,
      };
    });
    setMappedItems(items);
    setStep('preview');
  };

  // ── Import ────────────────────────────────────────────────────────────────

  const handleImport = async () => {
    const validItems = mappedItems.filter((i) => i.valid);
    if (validItems.length === 0) { toast.error('No valid items to import'); return; }

    setStep('importing');
    setImportProgress(0);
    const errors: string[] = [];
    let successCount = 0;

    for (let i = 0; i < validItems.length; i++) {
      const item = validItems[i];
      const saved = await insertClaimItem({
        name: item.name,
        description: item.description,
        category: item.category || undefined,
        quantity: item.quantity,
        location: item.location,
        addedBy: item.addedBy,
        condition: item.condition,
        estimatedAge: item.estimatedAge,
        notes: item.notes,
        priceStatus: item.unitPrice > 0 ? 'priced' : 'unpriced',
        unitPrice: item.unitPrice,
        priceResearch: null,
        dateAdded: new Date().toISOString().split('T')[0],
        inputMethod: 'typed',
      }, claimId);
      if (saved) {
        successCount++;
      } else {
        errors.push(`Row ${i + 1}: "${item.name}" — failed to save`);
      }
      setImportProgress(Math.round(((i + 1) / validItems.length) * 100));
    }

    setImportErrors(errors);
    if (successCount > 0) {
      toast.success(`${successCount} item${successCount !== 1 ? 's' : ''} imported successfully`);
      onImportComplete(successCount);
    }
    if (errors.length > 0) {
      toast.error(`${errors.length} item${errors.length !== 1 ? 's' : ''} failed to import`);
    }
  };

  const validCount = mappedItems.filter((i) => i.valid).length;
  const invalidCount = mappedItems.length - validCount;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
          <div className="flex items-center gap-3">
            {step !== 'upload' && step !== 'importing' && (
              <button
                onClick={() => setStep(step === 'preview' ? 'map' : 'upload')}
                className="p-1.5 rounded-lg hover:bg-secondary transition-colors text-muted-foreground"
              >
                <ArrowLeft size={16} />
              </button>
            )}
            <FileSpreadsheet size={20} className="text-primary" />
            <div>
              <h2 className="text-base font-semibold text-foreground">Import from File</h2>
              <p className="text-xs text-muted-foreground">
                {step === 'upload' && 'Upload a CSV or Excel file'}
                {step === 'map' && `Map columns from "${fileName}"`}
                {step === 'preview' && `Preview ${mappedItems.length} rows`}
                {step === 'importing' && 'Importing items…'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-secondary transition-colors text-muted-foreground"
          >
            <X size={18} />
          </button>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-1 px-6 py-3 border-b border-border shrink-0">
          {(['upload', 'map', 'preview'] as const).map((s, idx) => (
            <React.Fragment key={s}>
              <div className={`flex items-center gap-1.5 text-xs font-medium ${step === s ? 'text-primary' : ((['upload', 'map', 'preview'].indexOf(step) > idx || step === 'importing') ? 'text-positive' : 'text-muted-foreground')}`}>
                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${step === s ? 'bg-primary text-primary-foreground' : ((['upload', 'map', 'preview'].indexOf(step) > idx || step === 'importing') ? 'bg-positive/20 text-positive' : 'bg-secondary text-muted-foreground')}`}>
                  {(['upload', 'map', 'preview'].indexOf(step) > idx || step === 'importing') ? '✓' : idx + 1}
                </span>
                <span className="hidden sm:inline capitalize">{s}</span>
              </div>
              {idx < 2 && <ChevronRight size={12} className="text-muted-foreground mx-1" />}
            </React.Fragment>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">

          {/* ── Step 1: Upload ── */}
          {step === 'upload' && (
            <div className="space-y-4">
              <div
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-all ${isDragging ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50 hover:bg-secondary/50'}`}
              >
                <Upload size={36} className={`mx-auto mb-3 ${isDragging ? 'text-primary' : 'text-muted-foreground'}`} />
                <p className="text-sm font-semibold text-foreground mb-1">Drop your file here or click to browse</p>
                <p className="text-xs text-muted-foreground">Supports .csv, .xlsx, and .xls files</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  className="hidden"
                  onChange={handleFileChange}
                />
              </div>

              <div className="bg-secondary/50 rounded-xl p-4 space-y-2">
                <p className="text-xs font-semibold text-foreground">Tips for best results:</p>
                <ul className="text-xs text-muted-foreground space-y-1 list-disc list-inside">
                  <li>Include a header row with column names (e.g. "Item Name", "Quantity", "Price")</li>
                  <li>You'll be able to map each column to the correct field in the next step</li>
                  <li>Only "Item Name" is required — all other fields are optional</li>
                  <li>Prices should be numeric (e.g. 299.99 or $299.99)</li>
                </ul>
              </div>
            </div>
          )}

          {/* ── Step 2: Map columns ── */}
          {step === 'map' && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Your file has <span className="font-semibold text-foreground">{rows.length} rows</span> and <span className="font-semibold text-foreground">{headers.length} columns</span>. Map each field below to the matching column in your file.
              </p>

              <div className="space-y-3">
                {ITEM_FIELDS.map((field) => (
                  <div key={field.key} className="flex items-center gap-3">
                    <div className="w-40 shrink-0">
                      <p className="text-sm font-medium text-foreground">
                        {field.label}
                        {field.required && <span className="text-negative ml-1">*</span>}
                      </p>
                      <p className="text-xs text-muted-foreground">{field.description}</p>
                    </div>
                    <ChevronRight size={14} className="text-muted-foreground shrink-0" />
                    <select
                      value={columnMap[field.key] ?? ''}
                      onChange={(e) => handleMapChange(field.key, e.target.value)}
                      className="flex-1 px-3 py-2 text-sm border border-border rounded-lg bg-input focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value="">— Skip this field —</option>
                      {headers.map((h) => (
                        <option key={h} value={h}>{h}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>

              {!columnMap['name'] && (
                <div className="flex items-center gap-2 p-3 bg-negative/10 border border-negative/20 rounded-lg text-xs text-negative">
                  <AlertCircle size={14} />
                  <span>You must map the <strong>Item Name</strong> field to continue.</span>
                </div>
              )}
            </div>
          )}

          {/* ── Step 3: Preview ── */}
          {step === 'preview' && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle2 size={16} className="text-positive" />
                  <span className="font-semibold text-positive">{validCount} valid</span>
                </div>
                {invalidCount > 0 && (
                  <div className="flex items-center gap-2 text-sm">
                    <AlertCircle size={16} className="text-negative" />
                    <span className="font-semibold text-negative">{invalidCount} will be skipped</span>
                  </div>
                )}
              </div>

              <div className="border border-border rounded-xl overflow-hidden">
                <div className="overflow-x-auto max-h-72">
                  <table className="w-full text-xs">
                    <thead className="bg-secondary sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left font-semibold text-muted-foreground">Status</th>
                        <th className="px-3 py-2 text-left font-semibold text-muted-foreground">Item Name</th>
                        <th className="px-3 py-2 text-left font-semibold text-muted-foreground">Qty</th>
                        <th className="px-3 py-2 text-left font-semibold text-muted-foreground">Price</th>
                        <th className="px-3 py-2 text-left font-semibold text-muted-foreground">Location</th>
                        <th className="px-3 py-2 text-left font-semibold text-muted-foreground">Added By</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {mappedItems.map((item, idx) => (
                        <tr key={idx} className={item.valid ? '' : 'bg-negative/5'}>
                          <td className="px-3 py-2">
                            {item.valid
                              ? <CheckCircle2 size={13} className="text-positive" />
                              : <AlertCircle size={13} className="text-negative" title={item.error} />
                            }
                          </td>
                          <td className="px-3 py-2 font-medium text-foreground max-w-[160px] truncate">{item.name || <span className="text-negative italic">missing</span>}</td>
                          <td className="px-3 py-2 text-muted-foreground">{item.quantity}</td>
                          <td className="px-3 py-2 text-muted-foreground">{item.unitPrice > 0 ? `$${item.unitPrice.toFixed(2)}` : '—'}</td>
                          <td className="px-3 py-2 text-muted-foreground max-w-[100px] truncate">{item.location || '—'}</td>
                          <td className="px-3 py-2 text-muted-foreground max-w-[100px] truncate">{item.addedBy || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {invalidCount > 0 && (
                <p className="text-xs text-muted-foreground">
                  Rows without an item name will be skipped during import.
                </p>
              )}
            </div>
          )}

          {/* ── Step 4: Importing ── */}
          {step === 'importing' && (
            <div className="space-y-6 py-4">
              <div className="text-center space-y-3">
                {importProgress < 100 ? (
                  <>
                    <Loader2 size={36} className="animate-spin text-primary mx-auto" />
                    <p className="text-sm font-medium text-foreground">Importing items…</p>
                    <p className="text-xs text-muted-foreground">{importProgress}% complete</p>
                  </>
                ) : (
                  <>
                    <CheckCircle2 size={36} className="text-positive mx-auto" />
                    <p className="text-sm font-semibold text-foreground">Import complete!</p>
                  </>
                )}
                <div className="w-full bg-secondary rounded-full h-2 overflow-hidden">
                  <div
                    className="bg-primary h-2 rounded-full transition-all duration-300"
                    style={{ width: `${importProgress}%` }}
                  />
                </div>
              </div>

              {importErrors.length > 0 && (
                <div className="bg-negative/10 border border-negative/20 rounded-xl p-4 space-y-2">
                  <p className="text-xs font-semibold text-negative">Failed rows:</p>
                  <ul className="text-xs text-negative space-y-1">
                    {importErrors.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border flex justify-between items-center shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            {step === 'importing' && importProgress === 100 ? 'Close' : 'Cancel'}
          </button>

          {step === 'map' && (
            <button
              onClick={buildPreview}
              disabled={!columnMap['name']}
              className="flex items-center gap-2 px-5 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-semibold hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              Preview Import
              <ChevronRight size={15} />
            </button>
          )}

          {step === 'preview' && (
            <button
              onClick={handleImport}
              disabled={validCount === 0}
              className="flex items-center gap-2 px-5 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-semibold hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              <Upload size={15} />
              Import {validCount} Item{validCount !== 1 ? 's' : ''}
            </button>
          )}

          {step === 'importing' && importProgress === 100 && (
            <button
              onClick={onClose}
              className="flex items-center gap-2 px-5 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-semibold hover:bg-amber-700 transition-all"
            >
              <CheckCircle2 size={15} />
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
